import { DOCUMENT } from '@angular/common';
import { Directive, inject, OnInit, Renderer2 } from '@angular/core';
import { environment } from '../../../environments/environment';
import type { ICookiebotConfig } from '../../models/common.models';
import { AppUtils } from '../../utils/app.utils';

@Directive({
  selector: '[appCookiebot]',
  standalone: true,
})
export class CookiebotDirective implements OnInit {
  private readonly scriptId: string = 'Cookiebot';
  private readonly renderer2: Renderer2 = inject(Renderer2);
  private readonly document: Document = inject(DOCUMENT);
  private cookiebotConfig?: ICookiebotConfig;

  ngOnInit(): void {
    this.cookiebotConfig = environment.cookiebot;

    const cbid: string = this._getCurrentStoreCbid();

    if (!this._shouldInjectCookiebot(cbid)) {
      this._activateBlockedScripts();
      return;
    }

    this._injectScript(cbid);
  }

  private _activateBlockedScripts(): void {
    const scripts: NodeListOf<Element> = this.document.querySelectorAll('script[type="text/plain"][data-cookieconsent]');

    scripts.forEach(script => {
      const newScript: Element = this.renderer2.createElement('script');
      script.getAttributeNames().forEach((attr:string) => {
        if (attr !== 'type') {
          this.renderer2.setAttribute(newScript, attr, script.getAttribute(attr)!);
        }
      });

      this.renderer2.setAttribute(newScript, 'type', 'text/javascript');
      if (script.textContent) {
        newScript.textContent = script.textContent;
      }

      const parent: ParentNode | null = script.parentNode;
      if (parent) {
        this.renderer2.insertBefore(parent, newScript, script);
        this.renderer2.removeChild(parent, script);
      }
    });
  }

  private _shouldInjectCookiebot(cbid: string): boolean {
    const config: ICookiebotConfig | undefined = this.cookiebotConfig;

    return Boolean(
      config?.enabled &&
      config.scriptUrl &&
      cbid &&
      !this.document.getElementById(this.scriptId) &&
      !this._isAutomationBot(),
    );
  }

  private _injectScript(cbid: string): void {
    const scriptUrl: string = this.cookiebotConfig?.scriptUrl || '';

    if (!scriptUrl) {
      return;
    }

    const script: HTMLScriptElement = this.renderer2.createElement('script') as HTMLScriptElement;

    this.renderer2.setAttribute(script, 'id', this.scriptId);
    this.renderer2.setAttribute(script, 'src', scriptUrl);
    this.renderer2.setAttribute(script, 'data-cbid', cbid);
    this.renderer2.setAttribute(script, 'type', 'text/javascript');
    script.async = true;

    this.renderer2.appendChild(this.document.head || this.document.body, script);
  }

  private _getCurrentStoreCbid(): string {
    const config: ICookiebotConfig | undefined = this.cookiebotConfig;
    const currentStoreId: string | undefined = AppUtils.getCurrentStore()?.storeId;

    if (!currentStoreId || !config?.enabledStores?.includes(currentStoreId)) {
      return '';
    }

    return config.cbidByStore?.[currentStoreId] || '';
  }

  private _isAutomationBot(): boolean {
    const marker: string | undefined = this.cookiebotConfig?.automationUserAgentMarker;
    const userAgent: string = this.document.defaultView?.navigator?.userAgent || '';

    return Boolean(marker && userAgent.includes(marker));
  }
}
